import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalBaseDirective } from '$core/base/_modal.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import {
  NotificationsParams,
  NotificationTopic,
} from '$modules/personal/notifications/model/notifications-model';
import { Pagination } from '$core/models/pagination';
import { NotificationsHttpService } from '$modules/personal/notifications/notifications-http.service';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { BehaviorSubject, EMPTY, of, Subject } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';
import { SessionService } from '$core/services/session.service';
import { UserService } from '$core/services/user.service';
import { NotificationsDataService } from '$modules/personal/notifications/notifications-data.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SwalService } from '$core/shell/swal/swal.service';

@Component({
  selector: 'app-notification-settings-modal',
  templateUrl: './notification-settings-modal.component.html',
  styleUrls: ['./notification-settings-modal.component.scss'],
})
export class NotificationSettingsModalComponent
  extends ModalBaseDirective
  implements OnInit, OnDestroy
{
  public notificationTopics$ = new BehaviorSubject<
    Pagination<NotificationTopic | null>
  >(null);
  public isFirstLoading$ = new BehaviorSubject<boolean>(false);
  public loadingTopicIds = new Set<string>();
  public isInfinityScrollLoading$ = new BehaviorSubject<boolean>(false);
  private queryParams: NotificationsParams = {
    page: 1,
    per_page: 5,
  };
  public isLegal = false;
  public is_accept_advertisement: 0 | 1 = 0;
  public readonly onClose: Subject<{ is_accept_advertisement: 0 | 1 }> =
    new Subject();
  public errorTitle = '';
  public errorText = '';

  constructor(
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translateService: TranslateService,
    private readonly notificationsHttpService: NotificationsHttpService,
    private readonly notificationsDataService: NotificationsDataService,
    private sessionService: SessionService,
    private userService: UserService,
    protected swalService: SwalService
  ) {
    super(modalRef, modalService, translateService);
  }

  public ngOnInit() {
    this.getUser();
  }

  public getNotificationsTopics(params: NotificationsParams) {
    this.isFirstLoading$.next(true);
    this.notificationsHttpService
      .getNotificationsSettings({ params })
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isFirstLoading$.next(false)),
        catchError((error: HttpErrorResponse) => {
          this.errorTitle = this.translateService.instant(
            'AN_ERROR_HAS_OCCURRED'
          );
          this.errorText = error.error.message || 'Неизвестная ошибка';
          return of(null);
        })
      )
      .subscribe((result) => {
        this.notificationTopics$.next(result);
      });
  }

  private nextPage(nextPageUrl: string | null): void {
    if (!nextPageUrl) {
      return;
    }

    this.isInfinityScrollLoading$.next(true);
    this.notificationsHttpService
      .getNotificationsSettings({ url: nextPageUrl })
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isInfinityScrollLoading$.next(false))
      )
      .subscribe((res) => {
        const current = this.notificationTopics$.getValue();
        if (!current) {
          this.notificationTopics$.next(res);
        } else {
          this.notificationTopics$.next({
            ...res,
            data: [...current.data, ...res.data],
          });
        }
      });
  }

  public onScroll() {
    const notificationsData = this.notificationTopics$.getValue();
    if (notificationsData && notificationsData.next_page_url) {
      this.nextPage(notificationsData.next_page_url);
    }
  }

  public changeTopicStatus(id: string, is_subscribe: boolean) {
    this.loadingTopicIds.add(id);
    const request$ = is_subscribe
      ? this.notificationsHttpService.subscribeToTopic({ id })
      : this.notificationsHttpService.unsubscribeToTopic(id);

    request$
      .pipe(
        untilDestroyed(this),
        finalize(() => this.loadingTopicIds.delete(id))
      )
      .subscribe((updatedTopicList) => {
        const current = this.notificationTopics$.getValue();

        if (!current) {
          return;
        }

        const updatedTopic = updatedTopicList.data.find(
          (topic) => topic.id === id
        );

        this.notificationTopics$.next({
          ...current,
          ...updatedTopic,
        });
      });
  }

  public getUser(): void {
    this.sessionService.session$
      .pipe(
        filter((res) => !!res),
        switchMap((res) => {
          res.type === 'legal' ? (this.isLegal = true) : (this.isLegal = false);
          this.is_accept_advertisement = res.is_accept_advertisement;

          if (res.is_accept_advertisement === 1) {
            this.getNotificationsTopics(this.queryParams);
          }

          return EMPTY;
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  public changeConsentStatus(is_consent: boolean): void {
    this.userService
      .updateUser({ is_accept_advertisement: is_consent })
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.is_accept_advertisement = res.is_accept_advertisement;

        if (!res.is_accept_advertisement) {
          this.notificationsDataService.refresh$.next();
          this.onClose.next({
            is_accept_advertisement: res.is_accept_advertisement,
          });
        }
      });
  }

  public ngOnDestroy() {}
}
